// ---------------------------------------------------------------------------
// Part → team access
//
// One authority for the question "which teams are allowed to fix this part?".
// Three separate stores answer it, and before this module existed only one of
// them was ever consulted by allocation:
//
//   grading_part_teams      → an explicit assignment made in Parts Grading.
//                             This is the only source a person edits directly,
//                             so it is the one that must always be honoured.
//
//   team_part_references    → the per-team allowlist uploaded for client stock
//                             (part ids carrying a client prefix such as MCD-).
//                             Client stock is shared across teams, so the same
//                             part legitimately appears under several teams.
//
//   grading_reference.team  → the legacy single owning team from the ERP
//                             export. Useful, but it also carries values that
//                             are not teams at all: PROJECTS ONLY,
//                             RC CONSUMABLES, LOGISTICS MANAGED, NOT REPAIRED,
//                             RC ADMIN. Those are dropped — a part whose only
//                             label is one of them has no owning team, and
//                             work on it can never be "the wrong team's".
//
// The distinction between a *manual* grant and the others matters: allocation
// widens on a manual grant only. Treating the legacy fallback as a grant would
// silently admit every RC MCD-labelled part into RC MCD allocations that the
// reference list deliberately excludes.
// ---------------------------------------------------------------------------

import { GRADING_TEAM_OPTIONS } from "./gradingTeams";

export const PART_TEAM_SOURCE = {
  MANUAL: "manual",
  CLIENT_REF: "client-reference",
  REFERENCE: "reference",
};

// Order used whenever several sources grant the same team access.
const SOURCE_ORDER = [
  PART_TEAM_SOURCE.MANUAL,
  PART_TEAM_SOURCE.CLIENT_REF,
  PART_TEAM_SOURCE.REFERENCE,
];

const SUPPORTED_TEAMS = new Set(GRADING_TEAM_OPTIONS);

/** Strip the BOM the ERP export prefixes to its first cell, then normalise. */
export function cleanPartKey(value) {
  return String(value ?? "")
    .replace(/﻿/g, "")
    .trim()
    .toUpperCase();
}

function teamKey(value) {
  return String(value ?? "").trim().toUpperCase();
}

/** A real repair team, as opposed to a pseudo-team like PROJECTS ONLY. */
function isRepairTeam(value) {
  return SUPPORTED_TEAMS.has(teamKey(value));
}

function emptyEntry(partId) {
  return {
    partId,
    grade: "",
    manual: [],
    clientRef: [],
    reference: [],
    teams: [],
    sources: {},
    owned: false,
    // The raw grading_reference.team value, kept even when it is not a repair
    // team so the UI can explain why a part shows as unowned.
    legacyLabel: "",
  };
}

function grant(entry, team, source) {
  const key = teamKey(team);
  if (!isRepairTeam(key)) return;
  const bucket =
    source === PART_TEAM_SOURCE.MANUAL
      ? entry.manual
      : source === PART_TEAM_SOURCE.CLIENT_REF
        ? entry.clientRef
        : entry.reference;
  if (!bucket.includes(key)) bucket.push(key);
  if (!entry.sources[key]) entry.sources[key] = [];
  if (!entry.sources[key].includes(source)) entry.sources[key].push(source);
}

function finaliseEntry(entry) {
  entry.manual.sort();
  entry.clientRef.sort();
  entry.reference.sort();
  entry.teams = Object.keys(entry.sources).sort();
  entry.owned = entry.teams.length > 0;
  entry.teams.forEach((team) => {
    entry.sources[team].sort(
      (a, b) => SOURCE_ORDER.indexOf(a) - SOURCE_ORDER.indexOf(b),
    );
  });
  return entry;
}

/**
 * Build the index.
 *
 * @param gradingRows   rows from GET /api/grading — `part_id`, `team`, and
 *                      `manual_teams` (the explicit Parts Grading assignment).
 *                      `teams` is ignored: it already folds in the legacy
 *                      fallback and so cannot prove a manual assignment.
 * @param clientRefRows rows from team_part_references — `team`, `part_id`.
 */
export function buildPartTeamAccess({
  gradingRows = [],
  clientRefRows = [],
} = {}) {
  const byPart = Object.create(null);

  const ensure = (partId) => {
    if (!byPart[partId]) byPart[partId] = emptyEntry(partId);
    return byPart[partId];
  };

  for (const row of gradingRows || []) {
    const partId = cleanPartKey(row?.part_id);
    if (!partId) continue;
    const entry = ensure(partId);
    if (!entry.grade) entry.grade = String(row?.grade || "").toUpperCase();
    const legacy = teamKey(row?.team);
    if (legacy && !entry.legacyLabel) entry.legacyLabel = legacy;
    grant(entry, legacy, PART_TEAM_SOURCE.REFERENCE);
    for (const team of row?.manual_teams || []) {
      grant(entry, team, PART_TEAM_SOURCE.MANUAL);
    }
  }

  for (const row of clientRefRows || []) {
    const partId = cleanPartKey(row?.part_id);
    if (!partId) continue;
    grant(ensure(partId), row?.team, PART_TEAM_SOURCE.CLIENT_REF);
  }

  const byTeam = Object.create(null);
  GRADING_TEAM_OPTIONS.forEach((team) => {
    byTeam[team] = [];
  });
  const unowned = [];

  Object.keys(byPart)
    .sort()
    .forEach((partId) => {
      const entry = finaliseEntry(byPart[partId]);
      if (!entry.owned) {
        unowned.push(entry);
        return;
      }
      entry.teams.forEach((team) => {
        if (!byTeam[team]) byTeam[team] = [];
        byTeam[team].push(entry);
      });
    });

  return {
    byPart,
    byTeam,
    unowned,
    teams: [...GRADING_TEAM_OPTIONS],
    partCount: Object.keys(byPart).length,
    ownedCount: Object.keys(byPart).length - unowned.length,
  };
}

/** The entry for a part, or null when the part is not in the index. */
export function partAccessFor(access, partId) {
  const key = cleanPartKey(partId);
  if (!access?.byPart || !key) return null;
  return access.byPart[key] || null;
}

/** Any source grants the team access to the part. */
export function isTeamAllowedForPart(access, partId, team) {
  const entry = partAccessFor(access, partId);
  const key = teamKey(team);
  if (!entry || !key) return false;
  return entry.teams.includes(key);
}

/**
 * The team was assigned to the part by hand in Parts Grading.
 *
 * Allocation widens on this and on nothing else, which is what keeps the
 * change additive: no part that allocates today stops allocating.
 */
export function isTeamManuallyAllowed(access, partId, team) {
  const entry = partAccessFor(access, partId);
  const key = teamKey(team);
  if (!entry || !key || key === "ALL") return false;
  return entry.manual.includes(key);
}

/**
 * Judge one repair_centre_data row.
 *
 * `aligned`      — the engineer's team may fix the part.
 * `cross`        — somebody else's part; this is the contamination signal.
 * `unowned`      — the part has no owning team, so nothing can be judged.
 * `unknown-part` — the part is not in the grading reference at all.
 * `no-team`      — the record carries no engineer team.
 */
export function classifyRepairRecord(access, record) {
  const partId = cleanPartKey(record?.part_id);
  const engineerTeam = teamKey(record?.team);
  const entry = partAccessFor(access, partId);
  if (!engineerTeam) {
    return { status: "no-team", partId, engineerTeam: "", allowedTeams: entry?.teams || [] };
  }
  if (!entry) return { status: "unknown-part", partId, engineerTeam, allowedTeams: [] };
  if (!entry.owned) {
    return { status: "unowned", partId, engineerTeam, allowedTeams: [], entry };
  }
  return {
    status: entry.teams.includes(engineerTeam) ? "aligned" : "cross",
    partId,
    engineerTeam,
    allowedTeams: entry.teams,
    entry,
  };
}

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rate = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

/**
 * Cross-team work report over a set of repair_centre_data rows.
 *
 * `resolveEngineer` lets the caller fold alias spellings of one person into a
 * single row, which History already does everywhere else.
 */
export function buildCrossTeamReport({
  records = [],
  access = null,
  resolveEngineer = null,
} = {}) {
  const totals = {
    records: 0,
    aligned: 0,
    cross: 0,
    unowned: 0,
    unknownPart: 0,
    noTeam: 0,
    judged: 0,
    crossRate: 0,
    crossCommission: 0,
    crossBer: 0,
    crossNff: 0,
    crossEngineers: 0,
    crossParts: 0,
  };
  const engineers = Object.create(null);
  const pairs = Object.create(null);
  const teamTotals = Object.create(null);
  const parts = Object.create(null);
  const months = Object.create(null);
  const rows = [];

  const nameOf = (record) => {
    const raw = record?.engineer || "Unknown";
    if (typeof resolveEngineer !== "function") return raw;
    return resolveEngineer(raw, record) || raw;
  };

  const ensureTeam = (team) => {
    if (!teamTotals[team]) {
      teamTotals[team] = {
        team,
        // Work this team's engineers did on other teams' parts.
        incoming: 0,
        incomingCommission: 0,
        // Work other teams' engineers did on this team's parts.
        outgoing: 0,
        outgoingCommission: 0,
        net: 0,
        judged: 0,
      };
    }
    return teamTotals[team];
  };

  const ensureMonth = (month) => {
    if (!months[month]) {
      months[month] = { month, cross: 0, judged: 0, commission: 0, crossRate: 0 };
    }
    return months[month];
  };

  for (const record of records || []) {
    totals.records += 1;
    const verdict = classifyRepairRecord(access, record);
    const month = String(record?.month || "").trim() || "Unknown";
    const engineerTeam = verdict.engineerTeam;

    if (verdict.status === "no-team") {
      totals.noTeam += 1;
      continue;
    }
    if (verdict.status === "unknown-part") {
      totals.unknownPart += 1;
      continue;
    }
    if (verdict.status === "unowned") {
      totals.unowned += 1;
      continue;
    }

    totals.judged += 1;
    ensureMonth(month).judged += 1;
    ensureTeam(engineerTeam).judged += 1;

    if (verdict.status === "aligned") {
      totals.aligned += 1;
      continue;
    }

    // --- cross-team from here down -----------------------------------------
    const engineer = nameOf(record);
    const commission = num(record?.commission);
    const isBer = record?.is_ber === 1 || record?.is_ber === true;
    const status = String(record?.unit_status || "").trim().toUpperCase();
    const owningTeams = verdict.allowedTeams;

    totals.cross += 1;
    totals.crossCommission += commission;
    if (isBer) totals.crossBer += 1;
    if (status === "NFF") totals.crossNff += 1;

    const monthAgg = ensureMonth(month);
    monthAgg.cross += 1;
    monthAgg.commission += commission;

    const worker = ensureTeam(engineerTeam);
    worker.incoming += 1;
    worker.incomingCommission += commission;
    owningTeams.forEach((owner) => {
      const owned = ensureTeam(owner);
      owned.outgoing += 1;
      owned.outgoingCommission += commission;
    });

    if (!engineers[engineer]) {
      engineers[engineer] = {
        engineer,
        team: engineerTeam,
        cross: 0,
        judged: 0,
        commission: 0,
        ber: 0,
        nff: 0,
        crossRate: 0,
        partCounts: Object.create(null),
        partCommission: Object.create(null),
        owningCounts: Object.create(null),
        months: Object.create(null),
        parts: [],
        owningTeams: [],
      };
    }
    const eng = engineers[engineer];
    eng.cross += 1;
    eng.commission += commission;
    if (isBer) eng.ber += 1;
    if (status === "NFF") eng.nff += 1;
    eng.partCounts[verdict.partId] = (eng.partCounts[verdict.partId] || 0) + 1;
    eng.partCommission[verdict.partId] =
      (eng.partCommission[verdict.partId] || 0) + commission;
    eng.months[month] = (eng.months[month] || 0) + 1;
    owningTeams.forEach((owner) => {
      eng.owningCounts[owner] = (eng.owningCounts[owner] || 0) + 1;
    });

    owningTeams.forEach((owner) => {
      const key = `${engineerTeam}|${owner}`;
      if (!pairs[key]) {
        pairs[key] = {
          fromTeam: engineerTeam,
          toTeam: owner,
          count: 0,
          commission: 0,
          engineerSet: new Set(),
          partSet: new Set(),
        };
      }
      pairs[key].count += 1;
      pairs[key].commission += commission;
      pairs[key].engineerSet.add(engineer);
      pairs[key].partSet.add(verdict.partId);
    });

    if (!parts[verdict.partId]) {
      parts[verdict.partId] = {
        partId: verdict.partId,
        allowedTeams: owningTeams,
        grade: verdict.entry?.grade || "",
        count: 0,
        commission: 0,
        ber: 0,
        engineerSet: new Set(),
        teamSet: new Set(),
      };
    }
    const part = parts[verdict.partId];
    part.count += 1;
    part.commission += commission;
    if (isBer) part.ber += 1;
    part.engineerSet.add(engineer);
    part.teamSet.add(engineerTeam);

    rows.push({
      month,
      engineer,
      engineerTeam,
      partId: verdict.partId,
      grade: String(record?.grade || "").toUpperCase(),
      owningTeams: owningTeams.join(", "),
      commission,
      unitStatus: status,
      ber: isBer ? 1 : 0,
      completedAt: record?.completed_at || "",
    });
  }

  // Each engineer's judged total counts every judgeable record they booked,
  // not only the cross-team ones, so the rate is a share of their own work.
  for (const record of records || []) {
    const verdict = classifyRepairRecord(access, record);
    if (verdict.status !== "aligned" && verdict.status !== "cross") continue;
    const engineer = nameOf(record);
    if (engineers[engineer]) engineers[engineer].judged += 1;
  }

  totals.crossRate = rate(totals.cross, totals.judged);
  totals.crossEngineers = Object.keys(engineers).length;
  totals.crossParts = Object.keys(parts).length;

  const byEngineer = Object.values(engineers)
    .map((eng) => {
      const { partCounts, partCommission, owningCounts, months: em, ...rest } = eng;
      return {
        ...rest,
        crossRate: rate(eng.cross, eng.judged),
        parts: Object.keys(partCounts)
          .map((partId) => ({
            partId,
            count: partCounts[partId],
            commission: partCommission[partId],
          }))
          .sort((a, b) => b.count - a.count || a.partId.localeCompare(b.partId)),
        owningTeams: Object.keys(owningCounts)
          .map((team) => ({ team, count: owningCounts[team] }))
          .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team)),
        months: Object.keys(em)
          .map((month) => ({ month, count: em[month] }))
          .sort((a, b) => a.month.localeCompare(b.month)),
      };
    })
    .sort(
      (a, b) =>
        b.cross - a.cross ||
        b.commission - a.commission ||
        a.engineer.localeCompare(b.engineer),
    );

  const byTeamPair = Object.values(pairs)
    .map((pair) => ({
      fromTeam: pair.fromTeam,
      toTeam: pair.toTeam,
      count: pair.count,
      commission: pair.commission,
      engineers: pair.engineerSet.size,
      parts: pair.partSet.size,
    }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        a.fromTeam.localeCompare(b.fromTeam) ||
        a.toTeam.localeCompare(b.toTeam),
    );

  const byTeam = Object.values(teamTotals)
    .map((team) => ({
      ...team,
      // Positive means the team absorbed more of other teams' work than its
      // own parts were taken away by others.
      net: team.incoming - team.outgoing,
      incomingRate: rate(team.incoming, team.judged),
    }))
    .sort(
      (a, b) =>
        b.incoming + b.outgoing - (a.incoming + a.outgoing) ||
        a.team.localeCompare(b.team),
    );

  const byPart = Object.values(parts)
    .map((part) => ({
      partId: part.partId,
      allowedTeams: part.allowedTeams,
      grade: part.grade,
      count: part.count,
      commission: part.commission,
      ber: part.ber,
      engineers: [...part.engineerSet].sort(),
      teams: [...part.teamSet].sort(),
    }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        b.commission - a.commission ||
        a.partId.localeCompare(b.partId),
    );

  const byMonth = Object.values(months)
    .map((month) => ({
      month: month.month,
      cross: month.cross,
      judged: month.judged,
      commission: month.commission,
      crossRate: rate(month.cross, month.judged),
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  rows.sort(
    (a, b) =>
      a.month.localeCompare(b.month) ||
      a.engineer.localeCompare(b.engineer) ||
      a.partId.localeCompare(b.partId),
  );

  return { totals, byEngineer, byTeamPair, byTeam, byPart, byMonth, rows };
}
